<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER & BREADCRUMBS ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2">
          <span class="rm-badge-section">الرحلات</span>
          <div class="rm-breadcrumb">
            <router-link to="/trips" class="rm-bc-link">إدارة الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">{{ routeInfo.name || 'تفاصيل رحلات المسار' }}</span>
          </div>
        </div>
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="rm-code-badge rm-badge-lg">{{ routeInfo.route_code || ('R-' + String(routeId).padStart(4, '0')) }}</span>
          <h1 class="rm-main-title mb-0">{{ routeInfo.name }}</h1>
        </div>
        <p class="rm-subtitle mt-1">قائمة الرحلات المجدولة التابعة لهذا المسار ومواعيد انطلاقها وحالتها التشغيلية</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="$router.push('/trips')"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          العودة للمسارات
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportRouteTripsCsv"
          :disabled="trips.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          @click="goToCreateTrip"
        >
          <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
          إضافة رحلة جديدة
        </v-btn>
      </div>
    </div>

    <!-- ==================== ROUTE INFO STRIP ==================== -->
    <div class="rm-route-strip mb-6">
      <div class="rm-strip-item">
        <v-icon small color="#082C69" class="me-2">mdi-map-marker-distance</v-icon>
        <span class="text--secondary me-1">المسافة الكلية:</span>
        <span class="font-weight-bold text-navy">{{ routeInfo.canonical_distance ? `${routeInfo.canonical_distance} كم` : '—' }}</span>
      </div>
      <div class="rm-strip-divider"></div>
      <div class="rm-strip-item">
        <v-icon small color="#082C69" class="me-2">mdi-clock-time-four-outline</v-icon>
        <span class="text--secondary me-1">المدة التقريبية:</span>
        <span class="font-weight-bold text-navy">{{ routeInfo.canonical_duration || '—' }}</span>
      </div>
      <div class="rm-strip-divider"></div>
      <div class="rm-strip-item">
        <v-icon small color="#082C69" class="me-2">mdi-bus-stop</v-icon>
        <span class="text--secondary me-1">المحطات:</span>
        <span class="font-weight-bold text-navy">{{ routeInfo.start_stop || '—' }}</span>
        <v-icon x-small color="#94A3B8" class="mx-1">mdi-arrow-left</v-icon>
        <span class="font-weight-bold text-navy">{{ routeInfo.end_stop || '—' }}</span>
        <span class="rm-stops-pill ms-2">{{ routeInfo.stops_count || 0 }} محطات</span>
      </div>
    </div>

    <!-- ==================== 4 SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid rm-grid-4 mb-6">
      <!-- Card 1: عدد الرحلات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">عدد الرحلات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value">{{ summary.trips_count }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">إجمالي الرحلات المسجلة للمسار</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-bus-clock</v-icon>
        </div>
      </div>

      <!-- Card 2: أيام التشغيل -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">أيام التشغيل</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text">{{ summary.operating_days || '—' }}</span>
            <v-skeleton-loader v-else type="text" width="90" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">جدول أيام عمل حافلات المسار</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="26" color="#10B981">mdi-calendar-check-outline</v-icon>
        </div>
      </div>

      <!-- Card 3: أول رحلة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">أول رحلة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-navy">{{ formatTime(summary.first_trip_time) }}</span>
            <v-skeleton-loader v-else type="text" width="70" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">موعد انطلاق أول حافلة باليوم</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="26" color="#F59E0B">mdi-weather-sunset-up</v-icon>
        </div>
      </div>

      <!-- Card 4: آخر رحلة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">آخر رحلة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-navy">{{ formatTime(summary.last_trip_time) }}</span>
            <v-skeleton-loader v-else type="text" width="70" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">موعد انطلاق آخر حافلة باليوم</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-purple">
          <v-icon size="26" color="#8B5CF6">mdi-weather-sunset-down</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== PARENT TRIPS TABLE (7 EXACT COLUMNS) ==================== -->
    <div class="rm-table-card">
      <div class="rm-table-header d-flex align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <div class="rm-icon-header-box me-3">
            <v-icon size="20" color="#082C69">mdi-format-list-checks</v-icon>
          </div>
          <div>
            <h2 class="rm-card-title">جدول الرحلات التفصيلي</h2>
            <p class="rm-card-subtitle">كافة الرحلات المجدولة تحت مسار: {{ routeInfo.name }}</p>
          </div>
        </div>
        <span class="rm-table-count">عرض {{ trips.length }} رحلة</span>
      </div>

      <div class="rm-table-wrapper">
        <table class="rm-modern-table">
          <thead>
            <tr>
              <th class="rm-th-code">رقم الرحلة</th>
              <th class="rm-th-time text-center">وقت البداية</th>
              <th class="rm-th-time text-center">وقت النهاية</th>
              <th class="rm-th-duration text-center">المدة المتوقعة</th>
              <th class="rm-th-status text-center">الحالة</th>
              <th class="rm-th-actions text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="trip in trips"
              :key="trip.id"
              class="rm-table-row"
            >
              <!-- 1. رقم الرحلة -->
              <td class="rm-td-code">
                <span class="rm-trip-badge">{{ trip.trip_reference || ('T-' + String(trip.id).padStart(4, '0')) }}</span>
              </td>

              <!-- 2. وقت البداية -->
              <td class="rm-td-time text-center font-weight-bold">
                <span class="rm-time-pill text-navy">
                  <v-icon x-small color="#082C69" class="me-1">mdi-clock-start</v-icon>
                  {{ formatTime(trip.first_stop_time) }}
                </span>
              </td>

              <!-- 3. وقت النهاية -->
              <td class="rm-td-time text-center font-weight-bold">
                <span class="rm-time-pill text-navy">
                  <v-icon x-small color="#082C69" class="me-1">mdi-clock-end</v-icon>
                  {{ formatTime(trip.last_stop_time) }}
                </span>
              </td>

              <!-- 4. المدة المتوقعة -->
              <td class="rm-td-duration text-center">
                <span class="text-caption font-weight-medium">{{ trip.expected_duration || routeInfo.canonical_duration || '—' }}</span>
              </td>

              <!-- 5. الحالة -->
              <td class="rm-td-status text-center">
                <span
                  class="rm-status-badge"
                  :class="trip.status_id === 1 ? 'status-active' : 'status-paused'"
                >
                  <span class="rm-status-dot"></span>
                  {{ trip.status_name || (trip.status_id === 1 ? 'مجدولة' : 'متوقفة مؤقتًا') }}
                </span>
              </td>

              <!-- 6. الإجراءات -->
              <td class="rm-td-actions text-center">
                <div class="d-flex align-center justify-center gap-1">
                  <!-- 1. Primary Outlined Button: تقارير الرحلة -->
                  <v-btn
                    small
                    outlined
                    color="#082C69"
                    class="rm-btn-report me-1"
                    @click="openTripReports(trip)"
                  >
                    <v-icon small right class="ms-1">mdi-chart-box-outline</v-icon>
                    تقارير الرحلة
                  </v-btn>

                  <!-- 2. View Details (Eye) -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-icon-btn btn-view"
                        @click="goToTripDetails(trip.id)"
                      >
                        <v-icon small color="#082C69">mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>عرض تفاصيل الرحلة والمحطات</span>
                  </v-tooltip>

                  <!-- 3. Edit Trip (Pencil) -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-icon-btn btn-edit"
                        @click="goToEditTrip(trip.id)"
                      >
                        <v-icon small color="#3B82F6">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>تعديل بيانات الرحلة</span>
                  </v-tooltip>

                  <!-- 4. Duplicate Trip (Copy) -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-icon-btn btn-duplicate"
                        @click="duplicateTrip(trip)"
                      >
                        <v-icon small color="#6366F1">mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>نسخ الرحلة</span>
                  </v-tooltip>

                  <!-- 5. Pause / Reactivate -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-icon-btn"
                        :class="trip.status_id === 1 ? 'btn-pause' : 'btn-play'"
                        @click="confirmTogglePause(trip)"
                      >
                        <v-icon small :color="trip.status_id === 1 ? '#F59E0B' : '#10B981'">
                          {{ trip.status_id === 1 ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline' }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ trip.status_id === 1 ? 'إيقاف مؤقت للرحلة' : 'تفعيل الرحلة' }}</span>
                  </v-tooltip>

                  <!-- 6. Trash -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-icon-btn btn-trash"
                        @click="confirmTrashTrip(trip)"
                      >
                        <v-icon small color="#EF4444">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>حذف الرحلة</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="!isLoading && trips.length === 0">
              <td colspan="6" class="text-center py-12">
                <div class="rm-empty-state">
                  <v-icon size="54" color="#CBD5E1" class="mb-3">mdi-bus-clock</v-icon>
                  <h3 class="text-h6 font-weight-bold text--secondary mb-1">لا توجد رحلات مسجلة لهذا المسار</h3>
                  <p class="text-body-2 text--disabled mb-4">يمكنك إضافة رحلات جديدة وجدولتها بسهولة الآن.</p>
                  <v-btn
                    depressed
                    class="rm-btn-primary"
                    @click="goToCreateTrip"
                  >
                    <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
                    إضافة رحلات الآن
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ==================== TRIP REPORTS MODAL ==================== -->
    <v-dialog
      v-model="reportDialog.show"
      max-width="1180"
      content-class="rm-report-modal-dialog"
      scrollable
      persistent
    >
      <v-card class="rm-modal-card">
        <!-- Modal Header -->
        <div class="rm-modal-header d-flex align-center justify-space-between border-bottom">
          <div class="d-flex align-center gap-3">
            <div class="rm-modal-icon-box">
              <v-icon size="24" color="#082C69">mdi-chart-box-outline</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2 mb-1">
                <span class="rm-trip-badge">{{ reportDialog.trip ? reportDialog.trip.trip_reference : '—' }}</span>
                <span class="rm-code-badge">{{ routeInfo.route_code || '—' }}</span>
                <h2 class="rm-modal-title mb-0">تقرير أداء الرحلة التشغيلية</h2>
              </div>
              <p class="rm-modal-subtitle mb-0">
                {{ routeInfo.name }} &bull; موعد الانطلاق:
                <strong>{{ reportDialog.trip ? formatTime(reportDialog.trip.first_stop_time) : '—' }}</strong> &bull; الوصول:
                <strong>{{ reportDialog.trip ? formatTime(reportDialog.trip.last_stop_time) : '—' }}</strong>
              </p>
            </div>
          </div>

          <div class="d-flex align-center gap-2">
            <v-tooltip top :disabled="reportDialog.trips_table.data.length > 0">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="d-inline-block">
                  <v-btn
                    depressed
                    small
                    class="rm-btn-secondary"
                    @click="exportTripReportCsv"
                    :disabled="reportDialog.trips_table.data.length === 0"
                  >
                    <v-icon right size="16" class="ms-1">mdi-download</v-icon>
                    تصدير التقرير CSV
                  </v-btn>
                </span>
              </template>
              <span>لا توجد بيانات لتصديرها</span>
            </v-tooltip>

            <v-btn
              icon
              small
              @click="reportDialog.show = false"
              class="rm-close-btn"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Modal Body -->
        <v-card-text class="pa-5" style="max-height: 75vh;">
          <vue-element-loading :active="reportDialog.loading" spinner="bar-fade-scale" color="#082C69" />

          <!-- 1. Period Filters Toolbar -->
          <div class="rm-period-bar d-flex align-center justify-space-between flex-wrap gap-3 mb-4 p-3 rounded-lg">
            <div class="d-flex align-center gap-1">
              <span class="text-caption font-weight-bold text--secondary me-2">الفترة الزمنية:</span>
              <button
                type="button"
                v-for="p in periodOptions"
                :key="p.key"
                class="rm-filter-chip"
                :class="{ 'chip-active': reportDialog.period === p.key }"
                @click="setReportPeriod(p.key)"
              >
                {{ p.label }}
              </button>
            </div>

            <!-- Custom Date Pickers -->
            <div v-if="reportDialog.period === 'custom'" class="d-flex align-center gap-2">
              <input
                type="date"
                v-model="reportDialog.date_from"
                class="rm-date-input"
                @change="fetchTripReport(1)"
              />
              <span class="text-caption text--secondary">إلى</span>
              <input
                type="date"
                v-model="reportDialog.date_to"
                class="rm-date-input"
                @change="fetchTripReport(1)"
              />
            </div>
          </div>

          <!-- 2. Secondary Filters (Driver, Status, Payment, Search) -->
          <div class="rm-secondary-filters-grid mb-5">
            <!-- Driver Dropdown -->
            <div>
              <label class="rm-field-label">السائق</label>
              <select
                v-model="reportDialog.driver_id"
                class="rm-select-filter"
                @change="fetchTripReport(1)"
              >
                <option :value="null">جميع السائقين</option>
                <option
                  v-for="drv in reportDialog.available_drivers"
                  :key="drv.id"
                  :value="drv.id"
                >
                  {{ drv.name }} {{ drv.phone_number ? `(${drv.phone_number})` : '' }}
                </option>
              </select>
            </div>

            <!-- Execution Status Dropdown -->
            <div>
              <label class="rm-field-label">حالة التشغيل</label>
              <select
                v-model="reportDialog.status"
                class="rm-select-filter"
                @change="fetchTripReport(1)"
              >
                <option value="all">الكل</option>
                <option value="completed">منفذة</option>
                <option value="running">جاري التشغيل</option>
                <option value="unexecuted_past">غير منفذة (سوابق)</option>
                <option value="cancelled">ملغاة</option>
                <option value="upcoming">قادمة</option>
              </select>
            </div>

            <!-- Payment Method Dropdown -->
            <div>
              <label class="rm-field-label">طريقة الدفع</label>
              <select
                v-model="reportDialog.payment_method"
                class="rm-select-filter"
                @change="fetchTripReport(1)"
              >
                <option value="all">جميع الطرق</option>
                <option value="cash">نقدي فقط</option>
                <option value="online">إلكتروني / محفظة</option>
              </select>
            </div>

            <!-- Search Field -->
            <div>
              <label class="rm-field-label">بحث</label>
              <input
                type="text"
                v-model="reportDialog.search"
                placeholder="اسم السائق أو العميل..."
                class="rm-search-filter"
                @input="onReportSearch"
              />
            </div>
          </div>

          <!-- 3. 8 REAL KPI CARDS -->
          <div class="rm-cards-grid rm-grid-4 mb-6">
            <!-- KPI 1: إجمالي مرات التشغيل -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">مرات التشغيل</span>
                <span class="rm-stat-value">{{ reportDialog.kpis.total_executions || 0 }}</span>
                <span class="rm-stat-caption">إجمالي الرحلات المجدولة بالفترة</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-blue">
                <v-icon size="24" color="#082C69">mdi-bus-clock</v-icon>
              </div>
            </div>

            <!-- KPI 2: الرحلات المنفذة -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">الرحلات المنفذة</span>
                <span class="rm-stat-value text-success">{{ reportDialog.kpis.executed_trips || 0 }}</span>
                <span class="rm-stat-caption">رحلات اكتملت بنجاح</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-green">
                <v-icon size="24" color="#10B981">mdi-check-circle-outline</v-icon>
              </div>
            </div>

            <!-- KPI 3: الرحلات غير المنفذة -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">الرحلات غير المنفذة</span>
                <span class="rm-stat-value text-danger">{{ reportDialog.kpis.not_executed_trips || 0 }}</span>
                <span class="rm-stat-caption">ملغاة أو لم تنفذ بالموعد</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-red">
                <v-icon size="24" color="#EF4444">mdi-close-circle-outline</v-icon>
              </div>
            </div>

            <!-- KPI 4: إجمالي الحجوزات -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">إجمالي الحجوزات</span>
                <span class="rm-stat-value">{{ reportDialog.kpis.total_bookings || 0 }}</span>
                <span class="rm-stat-caption">عدد التذاكر المحجوزة</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-purple">
                <v-icon size="24" color="#8B5CF6">mdi-ticket-confirmation-outline</v-icon>
              </div>
            </div>

            <!-- KPI 5: إجمالي الركاب -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">إجمالي الركاب</span>
                <span class="rm-stat-value">{{ reportDialog.kpis.total_passengers || 0 }}</span>
                <span class="rm-stat-caption">الركاب المنقولين على المسار</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-blue">
                <v-icon size="24" color="#0284C7">mdi-account-group-outline</v-icon>
              </div>
            </div>

            <!-- KPI 6: التحصيل النقدي -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">التحصيل النقدي</span>
                <span class="rm-stat-value text-warning">{{ reportDialog.kpis.total_cash || 0 }} <small class="text-caption font-weight-bold">ج.م</small></span>
                <span class="rm-stat-caption">المبالغ النقدية المحصلة</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-amber">
                <v-icon size="24" color="#F59E0B">mdi-cash-multiple</v-icon>
              </div>
            </div>

            <!-- KPI 7: التحصيل الإلكتروني / المحفظة -->
            <div class="rm-stat-card">
              <div class="rm-stat-content">
                <span class="rm-stat-label">إلكتروني / محفظة</span>
                <span class="rm-stat-value text-navy">{{ reportDialog.kpis.total_other || 0 }} <small class="text-caption font-weight-bold">ج.م</small></span>
                <span class="rm-stat-caption">مدفوعات إلكترونية ومحافظ</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-teal">
                <v-icon size="24" color="#0D9488">mdi-credit-card-outline</v-icon>
              </div>
            </div>

            <!-- KPI 8: إجمالي الإيراد -->
            <div class="rm-stat-card rm-card-highlight">
              <div class="rm-stat-content">
                <span class="rm-stat-label">إجمالي الإيراد</span>
                <span class="rm-stat-value text-navy font-weight-black">{{ reportDialog.kpis.grand_total || 0 }} <small class="text-caption font-weight-bold">ج.م</small></span>
                <span class="rm-stat-caption">مجموع التحصيل الكلي</span>
              </div>
              <div class="rm-stat-icon-box rm-icon-blue">
                <v-icon size="24" color="#082C69">mdi-finance</v-icon>
              </div>
            </div>
          </div>

          <!-- 4. OCCURRENCES TABLE (Planned Trips) -->
          <div class="rm-table-card">
            <div class="rm-table-header d-flex align-center justify-space-between mb-3">
              <h3 class="rm-card-title">سجل تشغيل الرحلات الفعلي (Planned Trips)</h3>
              <span class="rm-table-count">{{ reportDialog.trips_table.total }} رحلة تشغيلية</span>
            </div>

            <div class="rm-table-wrapper report-table-scroll">
              <table class="rm-modern-table">
                <thead>
                  <tr>
                    <th class="text-center">تاريخ التشغيل</th>
                    <th class="text-center">موعد الانطلاق</th>
                    <th>السائق</th>
                    <th class="text-center">لوحة الحافلة</th>
                    <th class="text-center">الحجوزات</th>
                    <th class="text-center">التحصيل النقدي</th>
                    <th class="text-center">إلكتروني</th>
                    <th class="text-center">الحالة</th>
                    <th class="text-center">التفاصيل</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in reportDialog.trips_table.data"
                    :key="row.id"
                    class="rm-table-row"
                  >
                    <!-- Date -->
                    <td class="text-center font-weight-bold">
                      {{ row.planned_date }}
                    </td>

                    <!-- Time -->
                    <td class="text-center">
                      <span class="rm-time-pill text-navy">
                        {{ formatTime(row.departure_time || reportDialog.trip.first_stop_time) }}
                      </span>
                    </td>

                    <!-- Driver -->
                    <td>
                      <span class="font-weight-bold text-navy">{{ row.driver_name || 'بدون سائق' }}</span>
                      <span v-if="row.driver_phone" class="d-block text-caption text--secondary">{{ row.driver_phone }}</span>
                    </td>

                    <!-- Bus Plate -->
                    <td class="text-center">
                      <span class="text-caption font-weight-medium">{{ row.bus_plate || '—' }}</span>
                    </td>

                    <!-- Bookings -->
                    <td class="text-center font-weight-bold">
                      <span class="rm-count-pill">{{ row.bookings_count || 0 }}</span>
                    </td>

                    <!-- Cash -->
                    <td class="text-center font-weight-bold text-warning">
                      {{ row.cash_total || 0 }} ج.م
                    </td>

                    <!-- Other -->
                    <td class="text-center font-weight-bold text-navy">
                      {{ row.other_total || 0 }} ج.م
                    </td>

                    <!-- Status -->
                    <td class="text-center">
                      <span
                        class="rm-status-badge"
                        :class="`status-${row.status_code || 'running'}`"
                      >
                        <span class="rm-status-dot"></span>
                        {{ row.status_label || 'منفذة' }}
                      </span>
                    </td>

                    <!-- Actions -->
                    <td class="text-center">
                      <v-btn
                        x-small
                        depressed
                        class="rm-btn-secondary py-1 px-2"
                        @click="viewPlannedTripBookings(row)"
                      >
                        <v-icon x-small class="me-1">mdi-account-group</v-icon>
                        عرض الركاب
                      </v-btn>
                    </td>
                  </tr>

                  <!-- Empty State -->
                  <tr v-if="!reportDialog.loading && reportDialog.trips_table.data.length === 0">
                    <td colspan="9" class="text-center py-8 text--secondary">
                      لا توجد رحلات تشغيلية مسجلة خلال الفترة المحددة
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="reportDialog.trips_table.last_page > 1" class="d-flex align-center justify-center gap-2 mt-4 pt-3 border-top">
              <v-btn
                small
                icon
                :disabled="reportDialog.trips_table.page <= 1"
                @click="fetchTripReport(reportDialog.trips_table.page - 1)"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <span class="text-caption font-weight-bold text--secondary">
                صفحة {{ reportDialog.trips_table.page }} من {{ reportDialog.trips_table.last_page }}
              </span>
              <v-btn
                small
                icon
                :disabled="reportDialog.trips_table.page >= reportDialog.trips_table.last_page"
                @click="fetchTripReport(reportDialog.trips_table.page + 1)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- Modal Footer -->
        <v-card-actions class="d-flex align-center justify-space-between px-5 py-3 border-top bg-light">
          <span class="text-caption text--secondary">
            مبني على المحرك التشغيلي الموحد &bull; PlannedTrip Reports Service
          </span>
          <v-btn
            depressed
            class="rm-btn-secondary"
            @click="reportDialog.show = false"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== NESTED BOOKINGS DIALOG ==================== -->
    <v-dialog v-model="bookingsDialog.show" max-width="800" scrollable>
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center justify-space-between p-4 border-bottom">
          <div class="d-flex align-center gap-2">
            <v-icon color="#082C69">mdi-account-group</v-icon>
            <span class="font-weight-bold text-h6">قائمة ركاب الرحلة: {{ bookingsDialog.plannedTrip ? bookingsDialog.plannedTrip.planned_date : '' }}</span>
          </div>
          <v-btn icon small @click="bookingsDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4" style="max-height: 60vh;">
          <vue-element-loading :active="bookingsDialog.loading" spinner="bar-fade-scale" color="#082C69" />

          <table class="rm-modern-table">
            <thead>
              <tr>
                <th>اسم الراكب</th>
                <th>رقم الهاتف</th>
                <th>محطة الركوب</th>
                <th>محطة النزول</th>
                <th class="text-center">طريقة الدفع</th>
                <th class="text-center">المبلغ</th>
                <th class="text-center">حالة التحصيل</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="res in bookingsDialog.bookings" :key="res.id">
                <td class="font-weight-bold text-navy">{{ res.customer_name || 'راكب' }}</td>
                <td>{{ res.customer_phone || '—' }}</td>
                <td>{{ res.pickup_stop || '—' }}</td>
                <td>{{ res.dropoff_stop || '—' }}</td>
                <td class="text-center">
                  <span class="rm-pay-tag" :class="res.payment_method === 1 ? 'pay-cash' : 'pay-online'">
                    {{ res.payment_method === 1 ? 'نقدي' : 'إلكتروني' }}
                  </span>
                </td>
                <td class="text-center font-weight-bold">{{ res.price || 0 }} ج.م</td>
                <td class="text-center">
                  <v-icon x-small :color="res.is_collected ? '#10B981' : '#F59E0B'" class="me-1">
                    {{ res.is_collected ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                  </v-icon>
                  <span class="text-caption font-weight-bold">{{ res.is_collected ? 'تم التحصيل' : 'معلق' }}</span>
                </td>
              </tr>
              <tr v-if="!bookingsDialog.loading && bookingsDialog.bookings.length === 0">
                <td colspan="7" class="text-center py-6 text--secondary">
                  لا توجد حجوزات مسجلة لهذه الرحلة التشغيلية
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>

        <v-card-actions class="d-flex justify-end p-3 border-top">
          <v-btn depressed class="rm-btn-secondary" @click="bookingsDialog.show = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== CONFIRM ACTION DIALOG (PAUSE / TRASH / DUPLICATE) ==================== -->
    <v-dialog v-model="confirmDialog.show" max-width="480" persistent>
      <v-card class="pa-4" rounded="lg">
        <v-card-title class="d-flex align-center gap-2 pt-2">
          <v-icon :color="confirmDialog.iconColor" size="28">{{ confirmDialog.icon }}</v-icon>
          <span class="font-weight-bold text-h6">{{ confirmDialog.title }}</span>
        </v-card-title>
        <v-card-text class="pt-2 text-body-1" style="color: #475569;">
          {{ confirmDialog.message }}
        </v-card-text>
        <v-card-actions class="d-flex justify-end gap-2 pt-3">
          <v-btn
            depressed
            text
            class="font-weight-bold"
            :disabled="confirmDialog.loading"
            @click="confirmDialog.show = false"
          >
            إلغاء
          </v-btn>
          <v-btn
            depressed
            :color="confirmDialog.btnColor"
            class="white--text font-weight-bold px-4"
            :loading="confirmDialog.loading"
            @click="executeConfirmedAction"
          >
            {{ confirmDialog.btnText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingRouteTrips",
  components: {
    VueElementLoading,
  },
  data() {
    return {
      isLoading: false,
      routeId: null,
      routeInfo: {},
      summary: {
        trips_count: 0,
        operating_days: "—",
        first_trip_time: null,
        last_trip_time: null,
      },
      trips: [],
      confirmDialog: {
        show: false,
        type: "", // 'pause', 'reactivate', 'trash', 'duplicate'
        trip: null,
        title: "",
        message: "",
        icon: "",
        iconColor: "",
        btnText: "",
        btnColor: "",
        loading: false,
      },
      periodOptions: [
        { key: "today", label: "اليوم" },
        { key: "week", label: "الأسبوع" },
        { key: "month", label: "الشهر" },
        { key: "year", label: "السنة" },
        { key: "custom", label: "فترة مخصصة" },
      ],
      reportDialog: {
        show: false,
        loading: false,
        trip: null,
        period: "month",
        date_from: "",
        date_to: "",
        driver_id: null,
        status: "all",
        payment_method: "all",
        search: "",
        kpis: {
          total_executions: 0,
          executed_trips: 0,
          not_executed_trips: 0,
          total_bookings: 0,
          total_passengers: 0,
          total_cash: 0,
          total_other: 0,
          grand_total: 0,
        },
        trips_table: {
          data: [],
          total: 0,
          page: 1,
          per_page: 10,
          last_page: 1,
        },
        available_drivers: [],
        searchTimer: null,
      },
      bookingsDialog: {
        show: false,
        loading: false,
        plannedTrip: null,
        bookings: [],
      },
    };
  },
  created() {
    this.routeId = this.$route.params.route_id;
    if (this.routeId) {
      this.fetchRouteTrips();
    } else {
      this.$toast.error("معرف المسار غير محدد");
      this.$router.push("/trips");
    }
  },
  methods: {
    fetchRouteTrips() {
      this.isLoading = true;
      axios
        .get(`/trips/by-route/${this.routeId}`)
        .then((response) => {
          if (response.data) {
            this.routeInfo = response.data.route || {};
            this.summary = response.data.summary || {};
            this.trips = response.data.trips || [];
          }
        })
        .catch((error) => {
          console.error("Error fetching route trips:", error);
          this.$toast.error("تعذر تحميل رحلات المسار");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    goToTripDetails(tripId) {
      this.$router.push(`/trips/view-trip/trip=${tripId}`);
    },
    goToEditTrip(tripId) {
      this.$router.push(`/trips/edit/trip=${tripId}&action=edit`);
    },
    goToCreateTrip() {
      this.$router.push(`/trips/create?route_id=${this.routeId}`);
    },
    exportRouteTripsCsv() {
      exportToCsv({
        filename: `رحلات-${this.routeInfo.route_code || 'route'}-${new Date().toISOString().slice(0, 10)}.csv`,
        metadata: [
          { label: "كود المسار", value: this.routeInfo.route_code || `R-${this.routeId}` },
          { label: "اسم المسار", value: this.routeInfo.name || "" },
          { label: "المسافة الكلية", value: `${this.routeInfo.canonical_distance || 0} كم` },
          { label: "المدة التقريبية", value: this.routeInfo.canonical_duration || "" },
          { label: "إجمالي الرحلات المسجلة", value: this.trips.length },
        ],
        headers: [
          "رقم الرحلة",
          "المسار",
          "وقت البداية",
          "وقت النهاية",
          "المدة المتوقعة",
          "الحالة",
          "أيام التشغيل",
        ],
        rows: this.trips.map((t) => [
          t.trip_reference || `T-${String(t.id).padStart(4, "0")}`,
          this.routeInfo.name || "",
          this.formatTime(t.first_stop_time),
          this.formatTime(t.last_stop_time),
          t.expected_duration || this.routeInfo.canonical_duration || "—",
          t.status_name || (t.status_id === 1 ? "مجدولة" : "متوقفة مؤقتًا"),
          t.operating_days || "يوميًا",
        ]),
      });
    },

    /* ==================== TRIP REPORTS MODAL METHODS ==================== */
    openTripReports(trip) {
      this.reportDialog.trip = trip;
      this.reportDialog.period = "month";
      this.reportDialog.driver_id = null;
      this.reportDialog.status = "all";
      this.reportDialog.payment_method = "all";
      this.reportDialog.search = "";
      this.reportDialog.show = true;
      this.fetchTripReport(1);
    },
    setReportPeriod(periodKey) {
      this.reportDialog.period = periodKey;
      if (periodKey !== "custom") {
        this.fetchTripReport(1);
      }
    },
    onReportSearch() {
      clearTimeout(this.reportDialog.searchTimer);
      this.reportDialog.searchTimer = setTimeout(() => {
        this.fetchTripReport(1);
      }, 350);
    },
    fetchTripReport(page = 1) {
      if (!this.reportDialog.trip) return;
      this.reportDialog.loading = true;

      const params = {
        period: this.reportDialog.period,
        page: page,
        per_page: 10,
      };
      if (this.reportDialog.period === "custom") {
        if (this.reportDialog.date_from) params.date_from = this.reportDialog.date_from;
        if (this.reportDialog.date_to) params.date_to = this.reportDialog.date_to;
      }
      if (this.reportDialog.driver_id) params.driver_id = this.reportDialog.driver_id;
      if (this.reportDialog.status && this.reportDialog.status !== "all") params.status = this.reportDialog.status;
      if (this.reportDialog.payment_method && this.reportDialog.payment_method !== "all") params.payment_method = this.reportDialog.payment_method;
      if (this.reportDialog.search) params.search = this.reportDialog.search;

      axios
        .get(`/trips/${this.reportDialog.trip.id}/report`, { params })
        .then((res) => {
          if (res.data && res.data.success && res.data.data) {
            const data = res.data.data;
            this.reportDialog.kpis = data.kpis || this.reportDialog.kpis;
            this.reportDialog.trips_table = data.trips_table || this.reportDialog.trips_table;
            this.reportDialog.available_drivers = data.available_drivers || [];
          }
        })
        .catch((err) => {
          console.error("Failed to load trip report:", err);
          this.$toast.error("تعذر تحميل تقرير الرحلة");
        })
        .finally(() => {
          this.reportDialog.loading = false;
        });
    },
    exportTripReportCsv() {
      const trip = this.reportDialog.trip;
      if (!trip) return;

      const kpis = this.reportDialog.kpis;
      exportToCsv({
        filename: `تقرير-رحلة-${trip.trip_reference || trip.id}-${new Date().toISOString().slice(0, 10)}.csv`,
        metadata: [
          { label: "كود الرحلة", value: trip.trip_reference || `T-${trip.id}` },
          { label: "المسار", value: this.routeInfo.name || "" },
          { label: "الفترة المحددة", value: this.reportDialog.period },
          { label: "إجمالي مرات التشغيل", value: kpis.total_executions || 0 },
          { label: "الرحلات المنفذة", value: kpis.executed_trips || 0 },
          { label: "الرحلات غير المنفذة", value: kpis.not_executed_trips || 0 },
          { label: "إجمالي الحجوزات", value: kpis.total_bookings || 0 },
          { label: "إجمالي الركاب", value: kpis.total_passengers || 0 },
          { label: "التحصيل النقدي (ج.م)", value: kpis.total_cash || 0 },
          { label: "التحصيل الإلكتروني (ج.م)", value: kpis.total_other || 0 },
          { label: "إجمالي الإيراد (ج.م)", value: kpis.grand_total || 0 },
        ],
        headers: [
          "تاريخ التشغيل",
          "موعد الانطلاق",
          "السائق",
          "لوحة الحافلة",
          "الحجوزات",
          "التحصيل النقدي (ج.م)",
          "التحصيل الإلكتروني (ج.م)",
          "حالة التشغيل",
        ],
        rows: this.reportDialog.trips_table.data.map((row) => [
          row.planned_date,
          this.formatTime(row.departure_time || trip.first_stop_time),
          row.driver_name || "بدون سائق",
          row.bus_plate || "—",
          row.bookings_count || 0,
          row.cash_total || 0,
          row.other_total || 0,
          row.status_label || "منفذة",
        ]),
      });
    },
    viewPlannedTripBookings(plannedTrip) {
      this.bookingsDialog.plannedTrip = plannedTrip;
      this.bookingsDialog.loading = true;
      this.bookingsDialog.bookings = [];
      this.bookingsDialog.show = true;

      axios
        .get(`/planned-trips/${plannedTrip.id}/report-bookings`)
        .then((res) => {
          if (res.data && res.data.success && res.data.data) {
            this.bookingsDialog.bookings = res.data.data.bookings || [];
          }
        })
        .catch((err) => {
          console.error("Failed to load planned trip bookings:", err);
          this.$toast.error("تعذر تحميل ركاب هذه الرحلة");
        })
        .finally(() => {
          this.bookingsDialog.loading = false;
        });
    },

    /* ==================== ACTIONS (DUPLICATE, PAUSE, TRASH) ==================== */
    duplicateTrip(trip) {
      this.$router.push(`/trips/create?duplicate_from=${trip.id}&route_id=${this.routeId}`);
    },
    confirmTogglePause(trip) {
      const isCurrentlyActive = trip.status_id === 1;
      this.confirmDialog = {
        show: true,
        type: isCurrentlyActive ? "pause" : "reactivate",
        trip: trip,
        title: isCurrentlyActive ? "إيقاف الرحلة مؤقتاً" : "إعادة تفعيل الرحلة",
        message: isCurrentlyActive
          ? `هل أنت متأكد من رغبتك في إيقاف الرحلة (${trip.trip_reference}) مؤقتاً؟ لن يتم توليد أوقات تشغيل لها حتى يعاد تفعيلها.`
          : `هل تريد إعادة تفعيل الرحلة (${trip.trip_reference}) ومواصلة تشغيلها؟`,
        icon: isCurrentlyActive ? "mdi-pause-circle-outline" : "mdi-play-circle-outline",
        iconColor: isCurrentlyActive ? "#F59E0B" : "#10B981",
        btnText: isCurrentlyActive ? "إيقاف مؤقت" : "تفعيل",
        btnColor: isCurrentlyActive ? "warning" : "success",
        loading: false,
      };
    },
    confirmTrashTrip(trip) {
      this.confirmDialog = {
        show: true,
        type: "trash",
        trip: trip,
        title: "حذف الرحلة",
        message: `هل أنت متأكد من رغبتك في حذف الرحلة (${trip.trip_reference})؟ لن تظهر في النظام التشغيلي مجدداً.`,
        icon: "mdi-alert-circle-outline",
        iconColor: "#EF4444",
        btnText: "حذف الرحلة",
        btnColor: "error",
        loading: false,
      };
    },
    executeConfirmedAction() {
      const { type, trip } = this.confirmDialog;
      if (!trip) return;

      this.confirmDialog.loading = true;

      if (type === "pause") {
        axios
          .post(`/trips/${trip.id}/pause-parent`)
          .then((res) => {
            this.$toast.success(res.data.message || "تم إيقاف الرحلة مؤقتاً بنجاح");
            this.confirmDialog.show = false;
            this.fetchRouteTrips();
          })
          .catch((err) => {
            this.$toast.error(err.response?.data?.message || "تعذر إيقاف الرحلة");
          })
          .finally(() => {
            this.confirmDialog.loading = false;
          });
      } else if (type === "reactivate") {
        axios
          .post(`/trips/${trip.id}/reactivate-parent`)
          .then((res) => {
            this.$toast.success(res.data.message || "تمت إعادة تفعيل الرحلة بنجاح");
            this.confirmDialog.show = false;
            this.fetchRouteTrips();
          })
          .catch((err) => {
            this.$toast.error(err.response?.data?.message || "تعذر تفعيل الرحلة");
          })
          .finally(() => {
            this.confirmDialog.loading = false;
          });
      } else if (type === "trash") {
        axios
          .post(`/trips/${trip.id}/trash-parent`)
          .then((res) => {
            this.$toast.success(res.data.message || "تم حذف الرحلة بنجاح");
            this.confirmDialog.show = false;
            this.fetchRouteTrips();
          })
          .catch((err) => {
            this.$toast.error(err.response?.data?.message || "تعذر حذف الرحلة");
          })
          .finally(() => {
            this.confirmDialog.loading = false;
          });
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-flex;
  align-items: center;
  background: #E8EEF5;
  color: #082C69;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
}

.rm-breadcrumb {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: #64748B;
}

.rm-bc-link {
  color: #64748B;
  text-decoration: none;
  transition: color 0.2s;
}

.rm-bc-link:hover {
  color: #082C69;
}

.rm-bc-active {
  color: #082C69;
  font-weight: 600;
}

.rm-main-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 0.875rem;
  color: #64748B;
  margin: 0;
}

.rm-badge-lg {
  font-size: 1rem !important;
  padding: 4px 12px !important;
}

/* Buttons */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 40px !important;
  border-radius: 8px !important;
  padding: 0 18px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(8, 44, 105, 0.2) !important;
  transition: all 0.2s ease !important;
}

.rm-btn-primary:hover {
  background: #061B40 !important;
  transform: translateY(-1px);
}

.rm-btn-secondary {
  background: #FFFFFF !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  height: 40px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
}

.rm-btn-secondary:hover {
  background: #F8FAFC !important;
  border-color: #94A3B8 !important;
  color: #082C69 !important;
}

.rm-btn-report {
  font-weight: 700 !important;
  font-size: 12px !important;
  height: 32px !important;
  border-radius: 6px !important;
  text-transform: none !important;
  border-width: 1.5px !important;
}

.rm-btn-report:hover {
  background: rgba(8, 44, 105, 0.05) !important;
}

/* Route Strip */
.rm-route-strip {
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-strip-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.rm-strip-divider {
  width: 1px;
  height: 20px;
  background: #E2E8F0;
}

/* Cards Grid */
.rm-cards-grid {
  display: grid;
  gap: 16px;
}

.rm-grid-4 {
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 1200px) {
  .rm-grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .rm-grid-4 {
    grid-template-columns: 1fr;
  }
}

.rm-stat-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rm-card-highlight {
  background: #F8FAFC !important;
  border: 2px solid #082C69 !important;
}

.rm-stat-content {
  flex: 1;
}

.rm-stat-label {
  display: block;
  font-size: 0.775rem;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 2px;
}

.rm-stat-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #082C69;
  line-height: 1.2;
}

.rm-stat-value-text {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
}

.rm-stat-caption {
  display: block;
  font-size: 0.7rem;
  color: #94A3B8;
  margin-top: 2px;
}

.rm-stat-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
}

.rm-icon-blue { background: #EFF6FF; }
.rm-icon-green { background: #ECFDF5; }
.rm-icon-amber { background: #FFFBEB; }
.rm-icon-purple { background: #F5F3FF; }
.rm-icon-red { background: #FEF2F2; }
.rm-icon-teal { background: #F0FDFA; }

/* Table Card */
.rm-table-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-icon-header-box {
  width: 36px;
  height: 36px;
  background: #E8EEF5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-card-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 0.775rem;
  color: #64748B;
  margin: 0;
}

.rm-table-count {
  font-size: 0.8125rem;
  color: #64748B;
  font-weight: 600;
  background: #F8FAFC;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}

.rm-table-wrapper {
  overflow-x: auto;
}

.rm-modern-table {
  width: 100%;
  border-collapse: collapse;
}

.rm-modern-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 12px 14px;
  border-bottom: 2px solid #E2E8F0;
  white-space: nowrap;
}

.rm-modern-table td {
  padding: 12px 14px;
  font-size: 0.85rem;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.rm-table-row:hover {
  background-color: #F8FAFC;
}

/* Badges */
.rm-code-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.825rem;
  background: #EFF6FF;
  color: #082C69;
  border: 1px solid #DBEAFE;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-trip-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.825rem;
  background: #F1F5F9;
  color: #334155;
  border: 1px solid #CBD5E1;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-group-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.8rem;
  background: #EEF2FF;
  color: #4338CA;
  border: 1px solid #C7D2FE;
  padding: 2px 8px;
  border-radius: 6px;
}

.text-navy { color: #082C69 !important; }
.text-success { color: #10B981 !important; }
.text-danger { color: #EF4444 !important; }
.text-warning { color: #F59E0B !important; }

.rm-time-pill {
  display: inline-flex;
  align-items: center;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.825rem;
}

.rm-stops-pill {
  font-size: 0.7rem;
  font-weight: 600;
  background: #F1F5F9;
  color: #64748B;
  padding: 2px 7px;
  border-radius: 6px;
}

.rm-count-pill {
  display: inline-block;
  background: #F1F5F9;
  color: #082C69;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.rm-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.775rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 9999px;
  gap: 6px;
}

.rm-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-active, .status-completed {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}
.status-active .rm-status-dot, .status-completed .rm-status-dot {
  background: #10B981;
}

.status-paused, .status-upcoming {
  background: #FFFBEB;
  color: #92400E;
  border: 1px solid #FDE68A;
}
.status-paused .rm-status-dot, .status-upcoming .rm-status-dot {
  background: #F59E0B;
}

.status-running {
  background: #EFF6FF;
  color: #1E40AF;
  border: 1px solid #BFDBFE;
}
.status-running .rm-status-dot {
  background: #3B82F6;
}

.status-cancelled, .status-unexecuted_past {
  background: #FEF2F2;
  color: #991B1B;
  border: 1px solid #FECACA;
}
.status-cancelled .rm-status-dot, .status-unexecuted_past .rm-status-dot {
  background: #EF4444;
}

.rm-pay-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}
.pay-cash { background: #FEF3C7; color: #92400E; }
.pay-online { background: #CCFBF1; color: #0F766E; }

/* Report Button & Action Icons */
.rm-btn-report {
  height: 36px !important;
  border: 1px solid #082C69 !important;
  color: #082C69 !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
}

.rm-btn-report:hover {
  background: #E8EEF5 !important;
}

.rm-action-icon-btn {
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  border-radius: 8px !important;
  transition: all 0.2s;
}

.btn-view:hover { background: #EFF6FF !important; }
.btn-edit:hover { background: #EFF6FF !important; }
.btn-duplicate:hover { background: #EEF2FF !important; }
.btn-pause:hover { background: #FFFBEB !important; }
.btn-play:hover { background: #ECFDF5 !important; }
.btn-trash:hover { background: #FEF2F2 !important; }

/* Modal Styling */
::v-deep .rm-report-modal-dialog {
  max-width: min(1180px, calc(100vw - 64px)) !important;
  max-height: calc(100vh - 64px) !important;
  margin: 32px auto !important;
  border-radius: 18px !important;
  overflow: hidden !important;
}

.rm-modal-card {
  background: #FFFFFF;
  border-radius: 18px !important;
  overflow: hidden !important;
}

.rm-modal-header {
  padding: 22px 26px !important;
  background: #FFFFFF;
}

.report-table-scroll {
  overflow-x: auto !important;
  width: 100%;
}

.rm-modal-icon-box {
  width: 44px;
  height: 44px;
  background: rgba(8, 44, 105, 0.08);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-modal-title {
  font-size: 18px;
  font-weight: 800;
  color: #082C69;
}

.rm-modal-subtitle {
  font-size: 12px;
  color: #64748B;
}

.rm-period-bar {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.rm-filter-chip {
  background: #FFFFFF;
  border: 1px solid #CBD5E1;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rm-filter-chip:hover {
  border-color: #082C69;
  color: #082C69;
}

.rm-filter-chip.chip-active {
  background: #082C69;
  color: #FFFFFF;
  border-color: #082C69;
}

.rm-date-input,
.rm-select-filter,
.rm-search-filter {
  height: 36px;
  border: 1px solid #CBD5E1;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  background: #FFFFFF;
  outline: none;
}

.rm-select-filter,
.rm-search-filter {
  width: 100%;
}

.rm-secondary-filters-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 800px) {
  .rm-secondary-filters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.rm-field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 4px;
}

.bg-light {
  background: #F8FAFC !important;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
</style>
